import {
  PRIMARY_RESIDENCE, SECOND_HOME, SECOND_LOAN_SUB,
  AUTO, BOAT, CHILDCARE, HOME_EQUITY, INSTALLMENT, LEASE, LIENS, MORTGAGE,
  MOTORCYCLE, THIRTY_DAY_CHARGE_ACCT, OTHER_EXPENSES, OTHER_LIABILITIES,
  REVOLVING_CHARGE, RV, SEPARATE_MAINTENANCE, STUDENT_LOAN, TAXES, ALIMONY
} from "../../constants/liability_types";
import { OMITTED, WILL_BE_PAID_OFF } from "../../constants/account_dispositions";

const LIABILITY_TYPE_MISMO = {
  [PRIMARY_RESIDENCE]: "MortgageLoan",
  [SECOND_HOME]: "MortgageLoan",
  [SECOND_LOAN_SUB]: "MortgageLoan",
  [AUTO]: "Installment",
  [BOAT]: "Installment",
  [CHILDCARE]: "Other",
  [HOME_EQUITY]: "HELOC",
  [INSTALLMENT]: "Installment",
  [LEASE]: "LeasePayments",
  [LIENS]: "TaxLien",
  [MORTGAGE]: "MortgageLoan",
  [MOTORCYCLE]: "Installment",
  [THIRTY_DAY_CHARGE_ACCT]: "Open30DayChargeAccount",
  [OTHER_EXPENSES]: "Other",
  [OTHER_LIABILITIES]: "Other",
  [REVOLVING_CHARGE]: "Revolving",
  [RV]: "Installment",
  [SEPARATE_MAINTENANCE]: "SeparateMaintenance",
  [STUDENT_LOAN]: "StudentLoan",
  [TAXES]: "TaxLien",
  [ALIMONY]: "Alimony"
};

export function getLiabilityRecords(document) {
  return Object.values(document.accountSummaries || {}).filter(
    a => a.liability_type && parseFloat(a.monthly_payment) > 0
  );
}

// Each liability is written ONCE (one element per debt) and gets a stable
// label from its position in the record list. Ownership is expressed
// separately, as one RELATIONSHIP arc per owner — see exporters/relationships.
// This matches how real DU files (and Fannie Mae) represent a joint debt: a
// single LIABILITY with an arc to each borrower, NOT a duplicate element per
// borrower (which is what produced the double entries on round trips).
export function getLiabilitySeqNum(liabilityIndex) {
  return 1000 + (liabilityIndex + 1) * 10 + 1;
}

export function populateLiabilities(builder, { document }) {
  const allLiabilities = getLiabilityRecords(document);

  // Dispositions (Omit / Will Be Paid Off) are PER-LOAN and live on the loan
  // the LO selected on the Summary Sheet. LIABILITIES sits above the LOANS node
  // in the export, so there is no `loan` in context here - resolve the selected
  // loan the same way the rest of the export does. Without this every
  // LiabilityPayoffStatusIndicator / LiabilityExclusionIndicator exported false.
  const selectionNumber =
    document.summary && document.summary.selection_loan_number;
  const loan =
    selectionNumber && document.loans
      ? document.loans[parseInt(selectionNumber, 10) - 1]
      : null;

  allLiabilities.forEach((liability, liabilityIndex) => {
    const seqNum = getLiabilitySeqNum(liabilityIndex);
    const liab = builder.ele("LIABILITY");
    liab.att("SequenceNumber", String(seqNum));
    liab.att("xlink:label", `LIABILITY_${seqNum}`);

    const detail = liab.ele("LIABILITY_DETAIL");

    // Credit Limit — the credit report (and POINT) carry the revolving credit
    // limit in HELOCMaximumBalanceAmount, NOT LiabilityHighCreditAmount, even
    // for ordinary revolving cards (an industry quirk the credit vendors use).
    // Our importer reads it into high_credit_limit; POINT reads its "Credit
    // Limit" field from this same element. Must be first in LIABILITY_DETAIL
    // (alphabetical XSD order: HELOC... before Liability...).
    const creditLimit = parseFloat(liability.high_credit_limit) || 0;
    if (creditLimit > 0) {
      detail.ele("HELOCMaximumBalanceAmount").txt(creditLimit.toFixed(2));
    }

    if (liability.account_number) {
      detail.ele("LiabilityAccountIdentifier").txt(liability.account_number);
    }

    const disposition = loan && loan.account_dispositions
      ? loan.account_dispositions[liability.id]
      : null;
    const isExcluded = disposition === OMITTED;
    const isPaidOff = disposition === WILL_BE_PAID_OFF;

    detail.ele("LiabilityExclusionIndicator").txt(String(isExcluded));
    detail.ele("LiabilityMonthlyPaymentAmount").txt(
      String(parseFloat(liability.monthly_payment) || 0)
    );
    detail.ele("LiabilityPaymentIncludesTaxesInsuranceIndicator").txt("false");
    detail.ele("LiabilityPayoffStatusIndicator").txt(String(isPaidOff));

    const liabilityType = LIABILITY_TYPE_MISMO[liability.liability_type] || "Other";
    detail.ele("LiabilityType").txt(liabilityType);

    const balance = parseFloat(liability.current_balance) || 0;
    if (balance > 0) {
      detail.ele("LiabilityUnpaidBalanceAmount").txt(String(balance));
    }

    if (liability.description) {
      liab.ele("LIABILITY_HOLDER").ele("NAME").ele("FullName").txt(liability.description);
    }
  });
}
